import { omaSATOIdentityUtils } from '@sato/components';
import { AxiosHeaders, AxiosRequestConfig } from 'axios';

import { queryClient } from '@providers/QueryProvider';
import { authStore } from '@stores/authStore';
import { localizationStore } from '@stores/localization';
import { isLocalEnv } from '@utils/featureFlags';

import { authClient, publicClient } from './client.api';
import { handleApiError } from './SatoApiError';

const requestInterceptor = (config: AxiosRequestConfig) => ({
  ...config,
  // Test and prod uses cookies to authenticate. Since cookies are limited to the same domain,
  // we have to use bearer token authentication for localhost.
  headers: new AxiosHeaders({
    ...config.headers,
    'Content-Language': localizationStore.language,
    ...(isLocalEnv && {
      Authorization: `Bearer ${omaSATOIdentityUtils.getAccessToken()}`,
      AuthorizationID: omaSATOIdentityUtils.getIdToken() ?? '',
    }),
  }),
});

publicClient.interceptors.request.use(requestInterceptor);

publicClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isLocalEnv) console.error('[interceptor]:', error);

    // early-exit if errors should be ignored
    if (error.config?.ignoreErrors) return error.response;

    handleApiError(error);
  }
);

authClient.interceptors.request.use(async (requestConfig) => {
  // Renew auth tokens if cookies are expired (expired cookies get automatically removed by the browser)
  if (
    !omaSATOIdentityUtils.getAccessToken() ||
    !omaSATOIdentityUtils.getIdToken()
  ) {
    await authStore.refresh();
  }

  return requestInterceptor(requestConfig);
});

authClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isLocalEnv) console.error('[interceptor]:', error);

    // early-exit if errors should be ignored
    if (error.config?.ignoreErrors) return error.response;

    // auto-refresh auth tokens on 401 errors if
    if (
      error.response?.status === 401 &&
      // ... user can authenticate with a refresh token
      authStore.canAuthenticate
    ) {
      await authStore.refresh();

      // Invalidate queries so that failed queries can be fetched again
      // taken that refreshing credentials didn't fail
      queryClient.invalidateQueries();
    }

    handleApiError(error);
  }
);
